import { useState, useCallback, useEffect } from "react";
import { FolderGit, GitBranch } from "lucide-react";
import { Button } from "./ui/button";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "./ui/Modal";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { useToast } from "./ui/toast";
import { router } from "@inertiajs/react";

interface Repository {
  full_name: string;
  default_branch: string;
}

interface Branch {
  name: string;
  protected: boolean;
}

interface GithubRepository {
  uuid: string;
  owner: string;
  name: string;
  created_at: string;
  total_monthly_savings: number;
}

interface ConfigureGithubRepoModalProps {
  isOpen: boolean;
  onClose: () => void;
  workspaceSlug: string;
  onSuccess?: (repo: GithubRepository) => void;
}

export function ConfigureGithubRepoModal({
  isOpen,
  onClose,
  workspaceSlug,
  onSuccess,
}: ConfigureGithubRepoModalProps) {
  const { showToast } = useToast();
  const [selectedRepository, setSelectedRepository] = useState("");
  const [selectedBranch, setSelectedBranch] = useState("");
  const [repositories, setRepositories] = useState<Repository[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [defaultBranchToSelect, setDefaultBranchToSelect] = useState("");
  const [isLoadingRepos, setIsLoadingRepos] = useState(false);
  const [isLoadingBranches, setIsLoadingBranches] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchRepositories = useCallback(async () => {
    setIsLoadingRepos(true);
    try {
      const response = await fetch(`/workspaces/${workspaceSlug}/github/repositories`);
      const data = await response.json();

      if (data.repositories) {
        setRepositories(data.repositories);
      } else if (data.error) {
        showToast("error", data.error);
      }
    } catch (error) {
      console.error("Error fetching repositories:", error);
      showToast("error", "Failed to fetch repositories");
    } finally {
      setIsLoadingRepos(false);
    }
  }, [workspaceSlug, showToast]);

  const fetchBranches = useCallback(async (repositoryFullName: string, defaultBranch: string) => {
    setIsLoadingBranches(true);
    setBranches([]);
    setSelectedBranch("");
    setDefaultBranchToSelect(defaultBranch);
    try {
      const response = await fetch(
        `/workspaces/${workspaceSlug}/github/branches?repository=${encodeURIComponent(repositoryFullName)}`
      );
      const data = await response.json();

      if (data.branches) {
        setBranches(data.branches);
      } else if (data.error) {
        showToast("error", data.error);
        setDefaultBranchToSelect("");
      }
    } catch (error) {
      console.error("Error fetching branches:", error);
      showToast("error", "Failed to fetch branches");
      setDefaultBranchToSelect("");
    } finally {
      setIsLoadingBranches(false);
    }
  }, [workspaceSlug, showToast]);

  // Auto-select default branch after branches are loaded
  useEffect(() => {
    if (branches.length > 0 && defaultBranchToSelect && !selectedBranch) {
      setSelectedBranch(defaultBranchToSelect);
    }
  }, [branches, defaultBranchToSelect, selectedBranch]);

  useEffect(() => {
    if (isOpen) {
      setSelectedRepository("");
      setSelectedBranch("");
      setBranches([]);
      setDefaultBranchToSelect("");
      fetchRepositories();
    }
  }, [isOpen, fetchRepositories]);

  useEffect(() => {
    if (selectedRepository) {
      const repo = repositories.find((r) => r.full_name === selectedRepository);
      if (repo) {
        fetchBranches(selectedRepository, repo.default_branch);
      }
    } else {
      setBranches([]);
      setSelectedBranch("");
      setDefaultBranchToSelect("");
    }
  }, [selectedRepository, repositories, fetchBranches]);

  const handleClose = useCallback(() => {
    if (!isSubmitting) {
      onClose();
    }
  }, [isSubmitting, onClose]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedRepository) {
      showToast("error", "Please select a repository");
      return;
    }

    if (!selectedBranch) {
      showToast("error", "Please select a branch");
      return;
    }

    setIsSubmitting(true);

    try {
      const [owner, name] = selectedRepository.split("/");
      const csrfToken = document
        .querySelector('meta[name="csrf-token"]')
        ?.getAttribute("content");

      const response = await fetch(`/workspaces/${workspaceSlug}/github_repositories`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken || "",
        },
        body: JSON.stringify({
          github_repository: {
            owner,
            name,
            branch: selectedBranch,
          },
        }),
      });

      const data = await response.json();

      if (data.success) {
        showToast("success", "Infrastructure repository added successfully");
        handleClose();
        if (onSuccess && data.github_repository) {
          onSuccess(data.github_repository);
        } else {
          router.reload();
        }
      } else {
        showToast("error", data.errors?.join(", ") || "Failed to add repository");
      }
    } catch (error) {
      console.error("Error adding repository:", error);
      showToast("error", "Failed to add repository");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      canClose={!isSubmitting}
      size="md"
    >
      <form onSubmit={handleSubmit}>
        <ModalHeader
          icon={<FolderGit className="size-6 text-primary" />}
          title="Add repository"
          subtitle="Select a repository from your GitHub organization."
        />

        <ModalBody>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Repository
              </label>
              <Select
                value={selectedRepository}
                onValueChange={setSelectedRepository}
                disabled={isLoadingRepos || isSubmitting}
              >
                <SelectTrigger leftIcon={<FolderGit className="w-4 h-4" />}>
                  <SelectValue placeholder={isLoadingRepos ? "Loading repositories..." : "Select a repository"} />
                </SelectTrigger>
                <SelectContent>
                  {repositories.map((repo) => (
                    <SelectItem key={repo.full_name} value={repo.full_name}>
                      {repo.full_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {repositories.length === 0 && !isLoadingRepos && (
                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                  No repositories found. Make sure GitHub is connected in workspace settings.
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Branch
              </label>
              <Select
                value={selectedBranch}
                onValueChange={setSelectedBranch}
                disabled={!selectedRepository || isLoadingBranches || isSubmitting}
              >
                <SelectTrigger leftIcon={<GitBranch className="w-4 h-4" />}>
                  <SelectValue placeholder={!selectedRepository ? "Select a repository first" : isLoadingBranches ? "Loading branches..." : "Select a branch"} />
                </SelectTrigger>
                <SelectContent>
                  {branches.map((branch) => (
                    <SelectItem key={branch.name} value={branch.name}>
                      {branch.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {branches.length === 0 && !isLoadingBranches && selectedRepository && (
                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                  No branches found for this repository.
                </p>
              )}
            </div>
          </div>
        </ModalBody>

        <ModalFooter>
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={isSubmitting || !selectedRepository || !selectedBranch}
          >
            {isSubmitting ? "Adding..." : "Add Repository"}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}
